import {
  isEditableKeyTarget,
  zoomHotkeyAction,
} from 'features/knowledgeDocumentViewer/components/viewer/shared/viewerHotkeys'
import { useCallback, useEffect } from 'react'

/**
 * Window keydown listener for document viewer shortcuts. Inactive tabs stay
 * mounted, so pass the tab's active state as `enabled`.
 */
export function useViewerKeyDown(
  enabled: boolean,
  onKeyDown: (event: KeyboardEvent) => void
) {
  useEffect(() => {
    if (!enabled) return
    const handleKeyDown = (event: KeyboardEvent) => {
      if (isEditableKeyTarget(event.target)) return
      onKeyDown(event)
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [enabled, onKeyDown])
}

type UseZoomHotkeysParams = {
  enabled: boolean
  onResetZoom: () => void
  onZoomIn: () => void
  onZoomOut: () => void
}

export function useZoomHotkeys({
  enabled,
  onResetZoom,
  onZoomIn,
  onZoomOut,
}: UseZoomHotkeysParams) {
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      const action = zoomHotkeyAction(event)
      if (!action) return
      event.preventDefault()
      if (action === 'reset') onResetZoom()
      else if (action === 'zoomIn') onZoomIn()
      else onZoomOut()
    },
    [onResetZoom, onZoomIn, onZoomOut]
  )
  useViewerKeyDown(enabled, handleKeyDown)
}
